import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  ExpandableSection,
  Modal,
  SpaceBetween,
  Spinner,
  Table,
  TableProps,
  TextFilter,
} from "@amzn/awsui-components-react/polaris";

import { RootState } from "../../app/rootReducer";
import { trackButtonClick } from "../../telemetry/telemetry";
import {
  BLANK_SENTINEL,
  BulkCatalogDefaultsRow,
  buildBulkCatalogDefaultsRows,
  closeModal,
  fetchFailed,
  fetchSucceeded,
  MAX_BULK_CATALOG_DEFAULTS_ASINS,
  resolveCatalogDefaultsForAsins,
  SkippedCatalogDefaultsAsin,
  submitBulkCatalogDefaults,
} from "./submitCatalogDefaultsConnector";
import { fetchAllPagesForCurrentQuery } from "./fetchAllPagesForCurrentQuery";

const COLUMN_DEFINITIONS: TableProps.ColumnDefinition<BulkCatalogDefaultsRow>[] =
  [
    {
      id: "asin",
      header: "ASIN",
      cell: (row) => row.asin,
      sortingField: "asin",
      width: 130,
    },
    {
      id: "programName",
      header: "Program",
      cell: (row) => row.programName || "-",
      sortingField: "programName",
      width: 130,
    },
    {
      id: "marketplace",
      header: "MP",
      cell: (row) => row.marketplace,
      sortingField: "marketplace",
      width: 70,
    },
    {
      id: "step",
      header: "Validation Step",
      cell: (row) => row.step,
      sortingField: "step",
      width: 180,
    },
    {
      id: "currentValue",
      header: "Current Value",
      cell: (row) => row.currentValue || "-",
      width: 180,
    },
    {
      id: "correctedValue",
      header: "Corrected Value",
      cell: (row) => row.correctedValue,
      width: 180,
    },
    {
      id: "reason",
      header: "Validation Reason",
      cell: (row) => row.reason || "-",
    },
  ];

const SKIPPED_COLUMN_DEFINITIONS: TableProps.ColumnDefinition<SkippedCatalogDefaultsAsin>[] =
  [
    {
      id: "asin",
      header: "ASIN",
      cell: (row) => row.asin,
      width: 130,
    },
    {
      id: "programName",
      header: "Program",
      cell: (row) => row.programName || "-",
      width: 140,
    },
    {
      id: "marketplace",
      header: "MP",
      cell: (row) => row.marketplace,
      width: 70,
    },
    {
      id: "reasonText",
      header: "Skip Reason",
      cell: (row) => row.reasonText,
    },
  ];

function matchesFilter(row: BulkCatalogDefaultsRow, needle: string): boolean {
  if (!needle) {
    return true;
  }
  const n = needle.toLowerCase();
  return (
    row.asin.toLowerCase().includes(n) ||
    row.step.toLowerCase().includes(n) ||
    row.currentValue.toLowerCase().includes(n) ||
    row.correctedValue.toLowerCase().includes(n) ||
    row.reason.toLowerCase().includes(n) ||
    row.programName.toLowerCase().includes(n) ||
    row.marketplace.toLowerCase().includes(n)
  );
}

export const SubmitCatalogDefaultsModal: React.FC = () => {
  const dispatch = useDispatch();
  const {
    isOpen,
    workingAsins,
    isFetching,
    fetchError,
    fetchTruncated,
    isResolving,
    resolveAttempted,
    resolvedTuples,
    isSubmitting,
    submissionCompleted,
    submissionSucceeded,
  } = useSelector((state: RootState) => state.submitCatalogDefaults);
  const queryString = useSelector(
    (state: RootState) => state.generateQuery.queryString
  );
  const editableAttributes = useSelector(
    (state: RootState) => state.porAsinList.editableAttributes
  );

  const [selectedRows, setSelectedRows] = useState<BulkCatalogDefaultsRow[]>(
    []
  );
  const [filterText, setFilterText] = useState<string>("");
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const abortRef = useRef<AbortController | null>(null);

  const userIsAuthorized = useMemo(
    () =>
      editableAttributes.some(
        (attr) =>
          attr.attributeName === "publish_correct_catalog_default" &&
          attr.authorized
      ),
    [editableAttributes]
  );

  useEffect(() => {
    if (!isOpen) {
      setSelectedRows([]);
      setFilterText("");
      setConfirmed(false);
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }
    }
  }, [isOpen]);

  // Fetch the working set when the modal was opened with no explicit
  // selection.
  useEffect(() => {
    if (!isOpen || !isFetching) {
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    let cancelled = false;
    fetchAllPagesForCurrentQuery({
      queryString: queryString ?? "",
      signal: controller.signal,
      maxAsins: MAX_BULK_CATALOG_DEFAULTS_ASINS,
    })
      .then(({ rows, truncated }) => {
        if (cancelled) {
          return;
        }
        dispatch(fetchSucceeded({ asins: rows, truncated }));
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        const message =
          err instanceof Error && err.name === "AbortError"
            ? "Fetch cancelled or timed out"
            : "Failed to load matching ASINs";
        dispatch(fetchFailed({ message }));
      });
    return () => {
      cancelled = true;
      controller.abort();
      abortRef.current = null;
    };
  }, [isOpen, isFetching, queryString, dispatch]);

  // Once we have a working set, resolve corrected values for its
  // unique shape tuples exactly once per open/fetch cycle.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (workingAsins.length === 0) {
      return;
    }
    if (isResolving || resolveAttempted) {
      return;
    }
    dispatch(resolveCatalogDefaultsForAsins(workingAsins));
  }, [isOpen, workingAsins, isResolving, resolveAttempted, dispatch]);

  const { rows: allRows, skipped } = useMemo(
    () => buildBulkCatalogDefaultsRows(workingAsins, resolvedTuples),
    [workingAsins, resolvedTuples]
  );

  const visibleRows = useMemo(
    () => allRows.filter((row) => matchesFilter(row, filterText.trim())),
    [allRows, filterText]
  );

  const eligibleAsinCount = useMemo(() => {
    const set = new Set<string>();
    for (const row of allRows) {
      set.add(`${row.asin}::${row.marketplace}`);
    }
    return set.size;
  }, [allRows]);

  const hasBlank = useMemo(
    () => allRows.some((row) => row.correctedValue === BLANK_SENTINEL),
    [allRows]
  );

  const submitDisabledReason: string | undefined = !userIsAuthorized
    ? "You do not have permission to publish catalog defaults."
    : isFetching || isResolving
    ? "Loading ASINs data"
    : selectedRows.length === 0
    ? "Select at least one attribute to submit."
    : !confirmed
    ? "Tick the Confirm Catalog Contribution checkbox to submit."
    : undefined;
  const submitDisabled = submitDisabledReason !== undefined || isSubmitting;

  const title = `Submit Catalog Defaults (${allRows.length} attribute${
    allRows.length === 1 ? "" : "s"
  } across ${eligibleAsinCount} eligible ASIN${
    eligibleAsinCount === 1 ? "" : "s"
  })`;

  const handleClose = () => {
    dispatch(closeModal());
  };

  const handleSubmit = () => {
    trackButtonClick("publishCorrectCatalogDefault", "SubmitBulk", {
      selectedCount: selectedRows.length,
      totalCount: allRows.length,
      eligibleAsinCount,
      skippedCount: skipped.length,
    });
    dispatch(submitBulkCatalogDefaults(selectedRows));
  };

  // Only auto-close on partial or full success. A total failure keeps
  // the modal open so the user can retry without re-selecting; the
  // flashbar carries the error detail.
  useEffect(() => {
    if (submissionCompleted && !isSubmitting && submissionSucceeded) {
      dispatch(closeModal());
    }
  }, [submissionCompleted, isSubmitting, submissionSucceeded, dispatch]);

  const loadingPhase = isFetching || isResolving;
  const loadingText = "Loading ASINs data";

  return (
    <Modal
      data-testid="submit-catalog-defaults-modal"
      visible={isOpen}
      onDismiss={handleClose}
      size="max"
      closeAriaLabel="Close bulk catalog defaults modal"
      header={title}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Checkbox
              checked={confirmed}
              onChange={({ detail }) => setConfirmed(detail.checked)}
              disabled={isSubmitting || loadingPhase}
            >
              Confirm Catalog Contribution
            </Checkbox>
            <Button
              variant="link"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={submitDisabled}
              disabledReason={submitDisabledReason}
              loading={isSubmitting}
              onClick={handleSubmit}
              data-testid="submit-catalog-defaults-modal-submit"
            >
              Submit Catalog Defaults ({selectedRows.length})
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="s" direction="vertical">
        {!userIsAuthorized && (
          <Alert type="warning" statusIconAriaLabel="Warning">
            You do not have permissions to publish catalog defaults. Please open
            a ticket to the LEAP team to request access.
          </Alert>
        )}
        {fetchError && (
          <Alert type="error" statusIconAriaLabel="Error">
            {fetchError}
          </Alert>
        )}
        {fetchTruncated && !isFetching && (
          <Alert type="warning" statusIconAriaLabel="Warning">
            Only the first{" "}
            {MAX_BULK_CATALOG_DEFAULTS_ASINS.toLocaleString("en-US")} ASINs were
            loaded. Narrow the filter to include the remaining ASINs in a
            submission.
          </Alert>
        )}
        {hasBlank && (
          <Alert type="info" statusIconAriaLabel="Info">
            Some corrected values are {"<BLANK>"}. Default Value Corrections can
            only delete contributions from specific Device Vendors and Retail
            Merchants. After submitting your request, review Selection Central
            and manually delete contributions from other contributors as needed.
          </Alert>
        )}
        {loadingPhase ? (
          <Box textAlign="center" padding="l">
            <SpaceBetween size="s" direction="vertical" alignItems="center">
              <Spinner size="large" />
              <span>{loadingText}</span>
            </SpaceBetween>
          </Box>
        ) : (
          <>
            <TextFilter
              filteringText={filterText}
              filteringPlaceholder="Filter by ASIN, step, or attribute..."
              filteringAriaLabel="Filter attributes"
              onChange={({ detail }) => setFilterText(detail.filteringText)}
              countText={`${visibleRows.length} match${
                visibleRows.length === 1 ? "" : "es"
              }`}
            />
            <Table
              data-testid="submit-catalog-defaults-modal-table"
              variant="embedded"
              selectionType="multi"
              trackBy="key"
              items={visibleRows}
              selectedItems={selectedRows}
              onSelectionChange={({ detail }) =>
                setSelectedRows(detail.selectedItems)
              }
              columnDefinitions={COLUMN_DEFINITIONS}
              stickyHeader={true}
              resizableColumns={true}
              ariaLabels={{
                selectionGroupLabel: "Attribute selection",
                allItemsSelectionLabel: ({ selectedItems }) =>
                  `${selectedItems.length} attributes selected`,
                itemSelectionLabel: (_data, row) =>
                  `Select ${row.asin} ${row.step}`,
              }}
              empty={
                <Box textAlign="center" padding="m" color="text-body-secondary">
                  {allRows.length === 0
                    ? "No eligible catalog default corrections in the current scope."
                    : "No attributes match the current filter."}
                </Box>
              }
            />
            {skipped.length > 0 && (
              <ExpandableSection
                headerText={`Skipped ASINs (${skipped.length})`}
                defaultExpanded={true}
              >
                <Table
                  data-testid="submit-catalog-defaults-modal-skipped"
                  variant="embedded"
                  items={skipped}
                  columnDefinitions={SKIPPED_COLUMN_DEFINITIONS}
                  resizableColumns={true}
                />
              </ExpandableSection>
            )}
          </>
        )}
      </SpaceBetween>
    </Modal>
  );
};

export default SubmitCatalogDefaultsModal;
